import { useEffect, useState } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import type {
  UserDataDto,
  DocumentDto,
  ProcessDto,
} from '@cencert/mob-signature'
import { ProcessDtoLiteStatus } from '@cencert/mob-signature'
import { getProcessById } from '@/services/api'
import { useReturnToken } from './useReturnToken'

interface UseProcessDataReturn {
  userData: UserDataDto | null
  documents: DocumentDto[]
  isLoading: boolean
  error: string | null
}

/**
 * Hook for fetching and managing process data (user data and documents)
 * Integrates with useReturnToken for token-based data retrieval
 * Handles loading states and error scenarios
 */
export function useProcessData(
  onStatusChange?: (status: string | number | undefined) => void
): UseProcessDataReturn {
  const { t } = useTranslation()
  const [userData, setUserData] = useState<UserDataDto | null>(null)
  const [documents, setDocuments] = useState<DocumentDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    isProcessing: isTokenProcessing,
    isProcessed,
    processData: tokenProcessData,
  } = useReturnToken()

  const processAndSetData = (incomingProcessData: ProcessDto) => {
    if (!incomingProcessData) return

    // Check if status is not the expected one for Summary page
    if (
      incomingProcessData.status?.toString() !==
      ProcessDtoLiteStatus.WAITING_FOR_SUBJECT_ACCEPTANCE.toString()
    ) {
      // Notify parent about status change
      onStatusChange?.(incomingProcessData.status)
      return
    }

    // Extract and set user data
    if (incomingProcessData.userData) {
      setUserData(incomingProcessData.userData)
    }

    // Extract and set documents
    if (
      incomingProcessData.documents &&
      Array.isArray(incomingProcessData.documents)
    ) {
      setDocuments(incomingProcessData.documents)
    }

    setIsLoading(false)
  }

  // Effect for fetching process data from API
  useEffect(() => {
    if (
      isTokenProcessing ||
      (isProcessed && !!tokenProcessData) ||
      isFetching
    ) {
      return
    }

    const fetchProcessData = async () => {
      try {
        setIsLoading(true)
        setIsFetching(true)
        setError(null)

        const processData = await getProcessById()
        processAndSetData(processData)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching process data:', err)
        setError(t('summary.document.fetchError'))
        setIsLoading(false)
      } finally {
        setIsFetching(false)
      }
    }

    fetchProcessData()
  }, [isTokenProcessing, isProcessed, t])

  // Effect for processing token-based data
  useEffect(() => {
    if (isProcessed && tokenProcessData) {
      try {
        processAndSetData(tokenProcessData)
        setIsLoading(false)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error processing return token data:', err)
        setError('Błąd podczas przetwarzania danych użytkownika')
        setIsLoading(false)
      }
    }
  }, [isProcessed, tokenProcessData])

  return {
    userData,
    documents,
    isLoading: isLoading || isTokenProcessing,
    error,
  }
}
